import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type {
  AuthPayload,
  LoginInput,
  RegisterInput,
} from "../shared/auth";
import { ApiError, apiRequest } from "./api";

export type AuthStatus = "loading" | "anonymous" | "authenticated" | "error";

interface AuthValue {
  status: AuthStatus;
  user: AuthPayload["user"] | null;
  workspace: AuthPayload["workspace"] | null;
  bootError: string | null;
  login: (input: LoginInput) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [payload, setPayload] = useState<AuthPayload | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [bootError, setBootError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const session = await apiRequest<AuthPayload>("/api/v1/auth/session");
      setPayload(session);
      setStatus("authenticated");
      setBootError(null);
    } catch (error) {
      setPayload(null);
      if (error instanceof ApiError && error.status === 401) {
        setStatus("anonymous");
        setBootError(null);
      } else {
        setStatus("error");
        setBootError(error instanceof Error ? error.message : "Session unavailable");
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (input: LoginInput) => {
    const session = await apiRequest<AuthPayload>("/api/v1/auth/login", {
      method: "POST",
      body: input,
    });
    setPayload(session);
    setStatus("authenticated");
    setBootError(null);
  }, []);

  const register = useCallback(async (input: RegisterInput) => {
    const session = await apiRequest<AuthPayload>("/api/v1/auth/register", {
      method: "POST",
      body: input,
    });
    setPayload(session);
    setStatus("authenticated");
    setBootError(null);
  }, []);

  const logout = useCallback(async () => {
    await apiRequest<{ signedOut: boolean }>("/api/v1/auth/logout", {
      method: "POST",
      body: {},
    });
    setPayload(null);
    setStatus("anonymous");
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      bootError,
      login,
      logout,
      refresh,
      register,
      status,
      user: payload?.user ?? null,
      workspace: payload?.workspace ?? null,
    }),
    [bootError, login, logout, payload, refresh, register, status],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
